import {
  COLLABORATE_EXTERNAL_KEYS,
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_STATUS,
  EXPIRED_TRIAL_MESSAGE_MAX_ACTIVE_DAYS,
} from './const';
import { User, Subscription, SubscriptionConstruction } from './types';

export const isPaidSubscription = (
  subscription: SubscriptionConstruction,
  subscriptions: SubscriptionConstruction[],
) =>
  subscriptions.some(
    (current) =>
      !!current.offeringName &&
      current.offeringName === subscription.offeringName &&
      current.status === SUBSCRIPTION_STATUS.ACTIVE &&
      current.type !== SUBSCRIPTION_TYPES.TRIAL,
  );

const camelize = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-z\d]+(.)/gi, (m, chr) => chr.toUpperCase());
};

const generateProductName = (
  trial: Subscription | SubscriptionConstruction,
) => {
  const productName =
    'offeringCategoryName' in trial
      ? trial.offeringCategoryName
      : trial.offeringName;

  if (
    'offeringCategory' in trial &&
    trial.offeringExternalKey === COLLABORATE_EXTERNAL_KEYS.COLLABORATE_PRO
  ) {
    return productName + ' Pro';
  }
  return productName;
};

const reducer = (
  result: { [key: string]: string | number },
  trial: Subscription | SubscriptionConstruction,
  index: number,
) => {
  result.trialsDisplayed = `${result.trialsDisplayed}${
    index !== 0 ? ', ' : ''
  }${generateProductName(trial)}`;
  result[`${camelize(generateProductName(trial))}TrialDaysRemaining`] =
    trial.daysRemaining;
  return result;
};

const generateTrialsTrackingData = (
  trials: Subscription[] | SubscriptionConstruction[],
) => {
  return trials.reduce(reducer, { trialsDisplayed: '' });
};

export const getTrackingProperties = (
  user: User | null,
  trials: Subscription[] | SubscriptionConstruction[],
  currentProductId: string,
) => ({
  modalOpenTimeStamp: new Date(Date.now()).toISOString(),
  currentPage: currentProductId,
  currentUserEmailDomain:
    user && user.email.substring(user.email.lastIndexOf('@') + 1),
  ...generateTrialsTrackingData(trials),
});

export const addDaysToDate = (date: Date, days: number) => {
  const result = new Date(date);
  return result.setDate(result.getDate() + days);
};

export const shouldShowExpiredMessage = (
  trials: SubscriptionConstruction[],
) => {
  const lastTrialExpired = trials.reduce(
    (result: SubscriptionConstruction, trial: SubscriptionConstruction) => {
      if (result) {
        return new Date(result.endDate) < new Date(trial.endDate)
          ? trial
          : result;
      }

      return trial;
    },
  );
  const trialBannerHideDate = addDaysToDate(
    new Date(lastTrialExpired.endDate),
    EXPIRED_TRIAL_MESSAGE_MAX_ACTIVE_DAYS,
  );
  const today = new Date(Date.now()).setHours(0, 0, 0, 0);

  return today <= trialBannerHideDate;
};
